import { useState } from 'react'
import { useRouter } from 'next/router'
import { medusaClient } from '@lib/config'
import { useAccount } from '@lib/context/account-context'
import Button from '@modules/common/components/button'
import Input from '@modules/common/components/input'
import { FieldValues, useForm } from 'react-hook-form'
import { handleAuthError } from '@lib/util/handle-error'
import { Text } from '@chakra-ui/react'
import RouterUrl from '@constants/router.constant'
import AuthLayout from '@layouts/auth-layout'
import AuthForm from '../auth-form'
import DescriptionText from '../description-text'

interface SignInCredentials extends FieldValues {
  email: string
  password: string
}

const Login = () => {
  const { loginView, refetchCustomer } = useAccount()
  const [_, setCurrentView] = loginView
  const [authError, setAuthError] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const router = useRouter()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SignInCredentials>()

  const onSubmit = handleSubmit(async (credentials) => {
    setAuthError('')
    setLoading(true)
    medusaClient.auth
      .authenticate(credentials)
      .then(() => {
        refetchCustomer()
        setLoading(false)
        const arr = JSON.stringify(document.referrer).split('/')
        const path = arr.length > 0 ? arr[arr.length - 1] : ''
        if (
          path === 'reset-password' ||
          path === 'login' ||
          path === 'register' ||
          path.match(/^""$/) != null
        ) {
          router.push('/')
        } else {
          router.back()
        }
      })
      .catch((e) => {
        handleAuthError(e, (mess) => {
          setAuthError(mess)
        })
        setLoading(false)
      })
  })

  return (
    <AuthLayout
      title="Welcome back"
      description={
        <DescriptionText>
          Sign in to access an enhanced shopping experience.
        </DescriptionText>
      }
    >
      <AuthForm onSubmit={onSubmit} flexProps={{ gap: 4 }}>
        <Input
          label="Email"
          {...register('email', { required: 'Email is required' })}
          autoComplete="email"
          disabled={loading}
          errors={errors}
        />
        <Input
          label="Password"
          {...register('password', { required: 'Password is required' })}
          type="password"
          autoComplete="current-password"
          disabled={loading}
          errors={errors}
        />
        {authError && (
          <div>
            <span className="w-full text-rose-500 text-small-regular">
              {authError}
            </span>
          </div>
        )}
        <Text fontSize={'18px'}>
          <button
            onClick={() => {
              router.push(RouterUrl.AUTH.RESET_PASSWORD)
            }}
            className="underline"
            type="button"
          >
            Forgot password?
          </button>
        </Text>
        <Button isLoading={loading} disabled={loading}>
          Login
        </Button>
        <Text fontSize={'18px'} textAlign={'center'}>
          Not a member?{' '}
          <button
            onClick={() => {
              router.replace(RouterUrl.AUTH.REGISTER)
            }}
            className="underline"
            type="button"
          >
            Join us
          </button>
          . Get 5% off your first purchase.
        </Text>
      </AuthForm>
    </AuthLayout>
  )
}

export default Login
